import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckoutSteps } from "@/components/billing/CheckoutSteps";
import { db, money, packageFeatureLabels, type PackageRecord } from "@/lib/billing";
import { formatMoney, planMonthlyCents, type PlanPriceSlug } from "@/lib/currency";
import { useLocale, intlLocale, type Locale } from "@/lib/locale";
import { useAuth } from "@/hooks/useAuth";

async function fetchPackage(packageId: string): Promise<PackageRecord | null> {
  const { data, error } = await db.from("packages").select("*").eq("id", packageId).maybeSingle();
  if (error) throw error;
  return data;
}

/** Price shown for a package: the visitor's currency when the plan is in the list, else the package's own. */
function displayPrice(pkg: PackageRecord, uiLocale: Locale, locale: string): string {
  const base = pkg.slug.replace(/-v2$/, "") as PlanPriceSlug;
  const cents = (["starter", "growth", "network", "agency", "enterprise"] as string[]).includes(base) ? planMonthlyCents(base) : null;
  return cents ? formatMoney(cents, uiLocale) : money(pkg.monthly_price, pkg.currency, false, locale);
}

export default function CheckoutPackagePage() {
  const { t, i18n } = useTranslation();
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: pkg, isLoading, error } = useQuery({
    queryKey: ["checkout-package", packageId],
    queryFn: () => fetchPackage(packageId!),
    enabled: !!packageId,
  });

  const features = useMemo(() => {
    if (!pkg) return [];
    return Object.entries(packageFeatureLabels).map(([key, label]) => ({
      key,
      label: t(`packages.features.${key}`, { defaultValue: label }),
      enabled: Boolean(pkg.features?.[key]),
    }));
  }, [pkg, t]);

  const uiLocale = useLocale();
  const locale = intlLocale(uiLocale);
  const localizedLimit = (value: number | null | undefined) => value == null ? t("packages.custom") : value.toLocaleString(locale);

  const continueCheckout = () => {
    if (!pkg) return;
    sessionStorage.setItem("delapresse_checkout", JSON.stringify({ packageId: pkg.id }));
    navigate(`/info?package=${pkg.id}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 px-6 py-4 backdrop-blur-xl">
        <h1 className="text-lg font-semibold">{t("checkout.package.title")}</h1>
        <p className="text-xs text-muted-foreground">{t("checkout.package.subtitle")}</p>
      </header>
      <main className="mx-auto max-w-5xl space-y-6 p-6">
        <CheckoutSteps current={0} />
        {isLoading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : error || !pkg ? (
          <Card><CardContent className="p-6 text-sm text-destructive">{t("checkout.package.notFound")}</CardContent></Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">{pkg.description}</p>
                  </div>
                  {pkg.is_trial ? <Badge variant="secondary">{t("checkout.package.trial")}</Badge> : null}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-3 md:grid-cols-3">
                  <Metric label={t("packages.limits.websites")} value={localizedLimit(pkg.max_websites)} />
                  <Metric label={t("packages.limits.articles")} value={localizedLimit(pkg.max_articles_per_month)} />
                  <Metric label={t("packages.limits.aiCredits")} value={localizedLimit(pkg.max_ai_credits)} />
                  <Metric label={t("packages.limits.media")} value={localizedLimit(pkg.max_media_assets)} />
                  <Metric label={t("packages.limits.team")} value={localizedLimit(pkg.max_team_members)} />
                  <Metric label={t("packages.limits.support")} value={pkg.support_level} />
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {features.map((feature) => (
                    <div key={feature.key} className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
                      <span>{feature.label}</span>
                      <Badge variant={feature.enabled ? "default" : "secondary"}>{feature.enabled ? t("checkout.package.included") : t("checkout.package.notIncluded")}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">{t("checkout.package.summary")}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-md border border-border bg-muted/20 p-4">
                  <p className="text-3xl font-bold">{pkg.is_enterprise ? t("packages.custom") : displayPrice(pkg, uiLocale, locale)}</p>
                  <p className="text-xs text-muted-foreground">{t("checkout.package.monthlyBilling")}</p>
                </div>
                <p className="text-sm leading-6 text-muted-foreground">
                  {t("checkout.package.nextStep")}
                </p>
                {user ? (
                  <Button className="w-full" onClick={continueCheckout}>
                    {t("checkout.actions.continue")} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button asChild className="w-full"><Link to={`/dpadmin?package=${pkg.id}`}>{t("checkout.actions.signInToContinue")}</Link></Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  );
}
